import React, { useState, useEffect, useRef } from 'react';
import { isMobile } from 'react-device-detect';
import gsap from 'gsap/gsap-core';
import { Draggable } from 'gsap/Draggable';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';

import BottomNavRoom from './BottomNavRoom';
import IconItemRoom from './IconItemRoom';
import FixedIconRoom from './FixedIconRoom';
import { dragByScroll } from '../helpers';
import HeaderBraun from '../../common/HeaderBraun';
// styles
import styles from './Rooms.module.css';
// data
import rooms from '../../data/rooms';

// spa loader
import ComponentLoader from '../../SpaComponentLoader';

import { THEME, NAVIGATION_FROM } from '../../config/constants';

const Room = (props) => {
  const { data, back, prevData, nextData, getNewRoom } = props;

  const [zoomedIcon, setZoomedIcon] = useState(false);
  const [zoomable, setZoomable] = useState(false);
  const [zoomedIconConfig, setZoomedIconConfig] = useState({});
  const [currentImageWidth, setCurrentImageWidth] = useState(false);
  const [disableNavButtons, setDisableNavButtons] = useState(false);

  const [currRoomData, setCurrRoomData] = useState(data);
  const [hotspot, setHotspot] = useState(currRoomData.hotspot);

  const [prevRoomImage, setPrevRoomImage] = useState(prevData.image);
  const [currRoomImage, setCurrRoomImage] = useState(data.image);
  const [nextRoomImage, setNextRoomImage] = useState(nextData.image);

  const [showSpaComponentLoader, setShowSpaComponentLoader] = useState(false);
  const [currentComponentType, setCurrentComponentType] = useState(null);
  const [currentComponentData, setCurrentComponentData] = useState(null);

  const _isMobile = isMobile && typeof window !== 'undefined' && window.innerWidth < 1024;

  const webLeftDistance = '3.38vw';
  const mobileLeftDistance = '5.06vw';

  const webRightDistance = '93.75vw';
  const mobileRightDistance = '85.61vw';

  const toLeftDistance = _isMobile ? mobileRightDistance : webRightDistance;
  const toRightDistance = _isMobile ? mobileLeftDistance : webLeftDistance;

  const prevImageRef = useRef();
  const currentImageRef = useRef();
  const nextImageRef = useRef();

  const iterateThroughIconsAndAnimate = () => {
    if (typeof document !== 'undefined') {
      const listOfFixedIcons = document.getElementsByClassName('fixed-icon');
      Array.prototype.forEach.call(listOfFixedIcons, (el) => {
        if (el.classList.contains('right')) {
          gsap.fromTo(
            el,
            { left: '100vw' },
            { left: toLeftDistance, duration: 1, ease: 'easeIn' },
          );
        } else {
          gsap.fromTo(
            el,
            { left: '0' },
            { left: toRightDistance, duration: 1, ease: 'easeIn' },
          );
        }
      });
    }
  };

  const addDraggableToPage = () => {
    if (typeof document !== 'undefined') {
      const page = document.getElementById('roomPage');
      Draggable.create(page, {
        type: _isMobile ? 'scrollLeft' : 'left',
        allowContextMenu: true,
      });
    }
  };

  const checkAndDisplayHotspot = () => {
    if (hotspot) {
      setTimeout(() => {
        const top = _isMobile ? 'mobileTop' : 'top';
        const left = _isMobile ? 'mobileLeft' : 'left';
        if (typeof document !== 'undefined') {
          const roomHotspot = document.getElementById('roomHotspot');
          if (roomHotspot) {
            roomHotspot.style.top = hotspot[top];
            roomHotspot.style.left = hotspot[left];
          }
        }
      }, 500);
    }
  };

  const calculateSizeToScroll = (imageSize) => {
    if (typeof window !== `undefined` && typeof document !== `undefined`) {
      // Calculate the width for the viewport
      const vw = Math.max(
        document.documentElement.clientWidth || 0,
        window.innerWidth || 0,
        // return how much needs scrolling to center the image
      );
      return (imageSize - vw) / 2;
    }
  };

  useEffect(() => {
    gsap.registerPlugin(Draggable, ScrollToPlugin);

    if (typeof window !== `undefined` && typeof document !== `undefined`) {
      const vh = window.innerHeight * 0.01;
      const properSize = `${vh}px`;
      ['currImageId', 'nextImageId', 'prevImageId'].forEach((el) => {
        document.getElementById(el).style.setProperty('--vh', properSize);
      });
    }
    // initiate everything when the first image is loaded
    const firstImage = new Image();

    firstImage.src = currRoomImage;

    firstImage.onload = () => {
      const currentWidth = currentImageRef.current.getBoundingClientRect().width;

      setCurrentImageWidth(currentWidth);

      if (!_isMobile) {
        addDraggableToPage();
        dragByScroll(styles);
      }

      checkAndDisplayHotspot();

      iterateThroughIconsAndAnimate();

      gsap.to('#roomPage', {
        scrollTo: {
          x: calculateSizeToScroll(currentWidth),
        },
      });
    };
    firstImage.onerror = () => {
      console.log('error loading');
    };

    return () => {
      setZoomable(false);
      setZoomedIcon(false);
      if (typeof document !== 'undefined') {
        document.removeEventListener('click', clickHandlerZoom);
      }
    };
  }, []);

  useEffect(() => {
    if (zoomable) {
      if (typeof document !== 'undefined') {
        const roomPage = document.getElementById('roomPage');
        roomPage.addEventListener('click', clickHandlerZoom);
        roomPage.addEventListener('touchstart', () => {
          zoomOut();
        });
      }
    }
  }, [zoomedIcon, zoomable]);

  const clickHandlerZoom = (e) => {
    if (
      zoomedIcon &&
      !e.target.classList.contains('info-icon') &&
      !e.target.classList.contains('hotspot-icon')
    ) {
      zoomOut();
    } else if (e.target.classList.contains('hotspot-icon')) {
      zoomIn();
    }
  };

  const zoomIn = () => {
    if (typeof document !== 'undefined') {
      document.getElementById('roomPage').style.transform = 'scale(1.3)';
      document.getElementById('roomPage').style.transition = 'ease-in 1s';
      setZoomedIcon(true);

      const listOfFixedIcons = document.getElementsByClassName('hotspot-icon');
      Array.prototype.forEach.call(listOfFixedIcons, (el) => {
        el.style.opacity = 0;
      });

      const maxBraun = document.getElementById('roomHotspot');
      if (maxBraun) {
        maxBraun.style.opacity = 0;
      }
    }
  };

  const zoomOut = () => {
    if (typeof document !== 'undefined') {
      document.getElementById('roomPage').style.transform = 'scale(1)';
      document.getElementById('roomPage').style.transition = 'ease-out 1s';
      setZoomedIcon(false);

      const listOfFixedIcons = document.getElementsByClassName('hotspot-icon');
      Array.prototype.forEach.call(listOfFixedIcons, (el) => {
        el.style.opacity = 1;
      });

      const maxBraun = document.getElementById('roomHotspot');
      if (maxBraun) {
        maxBraun.style.opacity = 1;
      }
    }
  };

  const triggerFocusOnIcon = (config) => {
    setZoomable(true);
    setZoomedIconConfig(config);
  };

  const showPrevRoom = (roomId) => {
    gsap.killTweensOf('#roomPage');
    const newData = getNewRoom(roomId);
    setDisableNavButtons(true);
    gsap.set('.gradient-overlay', {
      left: _isMobile ? '-200vw' : '-100vw',
    });
    gsap.set('.on-screen-icon', { opacity: 0 });
    gsap.set('.fixed-icon', { opacity: 0 });
    gsap.set('#fixedIconList', { opacity: 0 });
    const prevRoomId = newData.links.previous.id;
    const nextRoomId = newData.links.next.id;
    const prevRoomData = rooms[prevRoomId];
    const nextRoomData = rooms[nextRoomId];

    const timeline = gsap.timeline({
      onComplete: () => {
        // put next room back to 50vw
        gsap.set('.prev-image', { left: '-20vw', maskPosition: '200%, 0' });
        gsap.set('.gradient-overlay', {
          left: _isMobile ? '-200vw' : '-100vw',
        });

        // updating images
        setPrevRoomImage(prevRoomData.image);
        setCurrRoomImage(newData.image);
        setNextRoomImage(nextRoomData.image);
        // show icons again
        gsap.to('.on-screen-icon', { opacity: 1, duration: 1 });
        gsap.set('#fixedIconList', { opacity: 1 });
        gsap.set('.fixed-icon', { opacity: 1 });
        iterateThroughIconsAndAnimate();
        setDisableNavButtons(false);
      },
    });

    // gradient
    timeline.to(
      '.gradient-overlay',
      { left: _isMobile ? '200vw' : '100vw', duration: 1.2 },
      0,
    );

    timeline.to(
      '.prev-image',
      { left: 0, maskPosition: '0%, 0', duration: 1 },
      0,
    );

    const previousWidthImage = prevImageRef.current.getBoundingClientRect()
      .width;

    timeline.to(
      '.background-holder',
      {
        width: previousWidthImage,
        duration: 0,
      },
      0,
    );

    timeline.set(
      '#roomPage',
      {
        scrollTo: {
          x: calculateSizeToScroll(previousWidthImage),
        },
        delay: 0.35,
      },
      0,
    );

    setTimeout(() => {
      setCurrRoomData(newData);
      setHotspot(prevRoomData.hotspot || currRoomData.hotspot);
      checkAndDisplayHotspot();
    }, 300);
  };

  const showNextRoom = (roomId) => {
    gsap.killTweensOf('#roomPage');
    const newData = getNewRoom(roomId);
    setDisableNavButtons(true);
    gsap.set('.gradient-overlay', {
      left: _isMobile ? '200vw' : '100vw',
    });
    gsap.set('.on-screen-icon', { opacity: 0 });
    gsap.set('.fixed-icon', { opacity: 0 });
    gsap.set('#fixedIconList', { opacity: 0 });

    const prevRoomId = newData.links.previous.id;
    const nextRoomId = newData.links.next.id;
    const prevRoomData = rooms[prevRoomId];
    const nextRoomData = rooms[nextRoomId];

    const timeline = gsap.timeline({
      onComplete: () => {
        // put next room back to 50vw
        gsap.set('.next-image', { left: '20vw', maskPosition: '-100%, 0' });
        gsap.set('.gradient-overlay', {
          left: _isMobile ? '100vw' : '-100vw',
        });
        // updating images
        setPrevRoomImage(prevRoomData.image);
        setCurrRoomImage(newData.image);
        setNextRoomImage(nextRoomData.image);
        // show icons again
        gsap.to('.on-screen-icon', { opacity: 1, duration: 1 });
        gsap.set('#fixedIconList', { opacity: 1 });
        gsap.set('.fixed-icon', { opacity: 1 });
        iterateThroughIconsAndAnimate();
        setDisableNavButtons(false);
      },
    });

    // gradient
    timeline.to(
      '.gradient-overlay',
      { left: _isMobile ? '-200vw' : '-100vw', duration: 1.2 },
      0,
    );

    timeline.to(
      '.next-image',
      { left: 0, maskPosition: '100%, 0', duration: 1 },
      0,
    );

    const nextWidthImage = nextImageRef.current.getBoundingClientRect().width;

    timeline.to(
      '.background-holder',
      {
        width: nextWidthImage,
        duration: 0,
      },
      0,
    );

    timeline.set(
      '#roomPage',
      {
        scrollTo: {
          x: calculateSizeToScroll(nextWidthImage),
        },
        delay: 0.35,
      },
      0,
    );

    setTimeout(() => {
      setCurrRoomData(newData);
      setHotspot(nextRoomData.hotspot || currRoomData.hotspot);
      checkAndDisplayHotspot();
    }, 400);
  };

  const openComponentInSpaMode = (type, componentData) => {
    // show component in SPA mode
    setShowSpaComponentLoader(true);
    setCurrentComponentType(type);
    setCurrentComponentData(componentData);
  };

  const zoomOutBeforeRoute = () => {
    setTimeout(() => {
      zoomOut();
    }, 500);
  };

  return (
    <>
      {/* loads compoenents in SPA mode, no Router */}
      {showSpaComponentLoader && (
        <ComponentLoader
          from={NAVIGATION_FROM.ROOM}
          type={currentComponentType}
          data={currentComponentData}
          onExitCompleted={() => {
            setShowSpaComponentLoader(false);
          }}
        />
      )}
      <HeaderBraun theme={THEME.WHITE} isRoom onBackToTimeline={back} />
      <div className={`gradient-overlay ${styles.gradient}`} />
      <div
        id="roomPage"
        className={`room-detail-${currRoomData.id} ${styles.overflowScroll}`}
      >
        {/* overlay */}
        <div className={styles.background} id="draggable">
          <div
            style={{ width: currentImageWidth }}
            className={`background-holder ${styles.backgroundImagesWrapper}`}
          >
            {/* prev */}
            <img
              id="prevImageId"
              className={`prev-image ${styles.prevImage} ${styles.image}`}
              ref={prevImageRef}
              src={prevRoomImage}
              alt=""
            />
            {/* curr */}
            <img
              id="currImageId"
              className={`curr-image ${styles.currImage} ${styles.image}`}
              ref={currentImageRef}
              src={currRoomImage}
              alt=""
            />
            {/* next */}
            <img
              id="nextImageId"
              className={`next-image ${styles.nextImage} ${styles.image}`}
              ref={nextImageRef}
              src={nextRoomImage}
              alt=""
            />
          </div>
          <div className="iconList" id="iconList">
            {currRoomData.icons &&
              currRoomData.icons.map((pic, i) => {
                return (
                  <IconItemRoom
                    key={i}
                    index={i}
                    isMobile={_isMobile}
                    triggerFocusOnIcon={triggerFocusOnIcon}
                    openDetail={openComponentInSpaMode}
                    {...pic}
                  />
                );
              })}
          </div>
          {/* [Max Braun Hotspot on 2040] */}
          {currRoomData.hotspot && !isMobile && (
            <div
              id="roomHotspot"
              className={styles.outHotCont}
              onClick={() => {
                openComponentInSpaMode(
                  currRoomData.hotspot.type,
                  currRoomData.hotspot.data,
                );
              }}
            >
              <div className={styles.outHotIcon}>
                <div className={styles.hotIcon} />
              </div>
            </div>
          )}
          {currRoomData.hotspot && isMobile && (
            <div
              id="roomHotspot"
              className={`${styles.outerContainerHotspotIcon} `}
              onClick={() => {
                openComponentInSpaMode(
                  currRoomData.hotspot.type,
                  currRoomData.hotspot.data,
                );
              }}
            >
              <div className={`${styles.outerHotspotIcon}`}>
                <div className={`${styles.hotspotIcon}`} />
              </div>
            </div>
          )}
        </div>
      </div>
      <>
        <div className="iconList" id="fixedIconList">
          {currRoomData.icons &&
            currRoomData.icons.map((pic, i) => {
              return (
                <FixedIconRoom
                  key={i}
                  index={i}
                  isMobile={_isMobile}
                  triggerFocusOnIcon={triggerFocusOnIcon}
                  zoomOutHandler={zoomOut}
                  {...pic}
                />
              );
            })}
        </div>
        <BottomNavRoom
          links={currRoomData.links}
          title={
            isMobile
              ? currRoomData.title.replace(/\s/g, '')
              : currRoomData.title
          }
          triggerFocusOnIcon={triggerFocusOnIcon}
          zoomedIconConfig={zoomedIconConfig}
          zoomedIcon={zoomedIcon}
          isMobile={isMobile}
          showPrevRoom={showPrevRoom}
          openDetail={openComponentInSpaMode}
          zoomOutHandler={zoomOutBeforeRoute}
          showNextRoom={showNextRoom}
          disableNavButtons={disableNavButtons}
        />
      </>
    </>
  );
};

export default Room;
